import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole, ScreenKey } from '../context/voiceConsoleContext';
import VoiceSidebar from './VoiceSidebar';
import VoiceTopbar from './VoiceTopbar';
import LimitHitBanner from '../components/LimitHitBanner';
import HomeScreen from '../screens/HomeScreen';
import BoardScreen from '../screens/BoardScreen';
import CallLogScreen from '../screens/CallLogScreen';
import FollowupsScreen from '../screens/FollowupsScreen';
import AnalyticsScreen from '../screens/AnalyticsScreen';
import IntentsScreen from '../screens/IntentsScreen';
import SettingsScreen from '../screens/SettingsScreen';
import LiveCallsScreen from '../screens/LiveCallsScreen';
import CatalogScreen from '../screens/CatalogScreen';
import RouterScreen from '../screens/RouterScreen';
import ProvidersScreen from '../screens/ProvidersScreen';
import PhoneKycScreen from '../screens/PhoneKycScreen';
import CallDetailScreen from '../screens/CallDetailScreen';
import OnboardScreen from '../screens/OnboardScreen';
import OutgoingScreen from '../screens/OutgoingScreen';
import CampaignsScreen from '../screens/CampaignsScreen';
import ObRulesScreen from '../screens/ObRulesScreen';
import DndScreen from '../screens/DndScreen';
import InboundRulesScreen from '../screens/InboundRulesScreen';
import ObReasonsScreen from '../screens/ObReasonsScreen';
import { useLiveCalls } from '../context/liveCallsContext';
import { listFollowups } from '../services/voice/voiceService';

const SCREENS: Record<ScreenKey, React.ComponentType> = {
  onboard: OnboardScreen,
  home: HomeScreen,
  board: BoardScreen,
  log: CallLogScreen,
  followups: FollowupsScreen,
  analytics: AnalyticsScreen,
  intents: IntentsScreen,
  settings: SettingsScreen,
  live: LiveCallsScreen,
  catalog: CatalogScreen,
  router: RouterScreen,
  providers: ProvidersScreen,
  phone: PhoneKycScreen,
  outgoing: OutgoingScreen,
  campaigns: CampaignsScreen,
  obrules: ObRulesScreen,
  dnd: DndScreen,
  inbound: InboundRulesScreen,
  obreasons: ObReasonsScreen,
};

export default function VoiceAppShell({ onSignOut }: { onSignOut?: () => void }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { screen, selectedCall, orgId } = useConsole();
  const Screen = SCREENS[screen];

  // Sidebar badges from real sources: live count from the SSE feed, open follow-ups
  // from the console API. Refetch follow-ups on org change and when the user lands
  // on the follow-ups screen (so resolving one updates the badge).
  const { calls } = useLiveCalls();
  const liveCount = calls.length;
  const [openFollowups, setOpenFollowups] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    listFollowups('OPEN').then(r => { if (alive) setOpenFollowups(r.data.length); }).catch(() => {});
    return () => { alive = false; };
  }, [orgId, screen]);

  // Fullscreen board mode for the LED wall (?board=1): drop the sidebar+topbar and
  // the scroll wrapper so BoardScreen fills the wall's native 1016×812 with no
  // chrome and no scrolling. Everything else (auth/contexts) stays intact.
  const boardMode =
    typeof window !== 'undefined' &&
    /[?&]board=1\b/.test(window.location.search);
  if (boardMode) {
    return (
      <View style={{ height: '100%', width: '100%', backgroundColor: '#0A0C10' }}>
        <BoardScreen />
      </View>
    );
  }

  return (
    <View style={{ flexDirection: 'row', height: '100%', width: '100%', backgroundColor: c.paper }}>
      <VoiceSidebar liveCount={liveCount} openFollowups={openFollowups} />
      <View style={{ flex: 1, minWidth: 0, height: '100%' }}>
        <VoiceTopbar onSignOut={onSignOut} />
        <LimitHitBanner />
        <View style={{ flex: 1, minHeight: 0 }}>
          <ScrollView style={{ flex: 1, backgroundColor: c.paper }} contentContainerStyle={{ flexGrow: 1 }}>
            {Screen ? <Screen /> : (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <Text style={{ color: c.ink3 }}>{screen}</Text>
              </View>
            )}
          </ScrollView>
          {/* Call Detail overlays the content column (top:56 left:236 in the HTML). */}
          {selectedCall && <CallDetailScreen />}
        </View>
      </View>
    </View>
  );
}
